/**
 * Registered battery-electric passenger cars (M1) by holder type, per region, at country /
 * state / postcode level. Sources: Kraftfahrt-Bundesamt FZ 27 (DE), BFS
 * Strassenfahrzeugbestand (CH), Statistik Austria Kfz-Bestand (AT).
 *
 * Numeric figures are nullable because a source can withhold a value (confidential KBA
 * figures, or a split Austria does not publish). A level counts as usable when its
 * `totalBevShare` is non-null — there is no completeness flag.
 */
import * as z from 'zod';

/** The three aggregation levels the dataset carries a record for. */
export const BEV_HOLDER_LEVELS = ['country', 'state', 'postcode'] as const;
export type BevHolderLevel = (typeof BEV_HOLDER_LEVELS)[number];

/**
 * `regionCode` is the dataset's descriptive key: the country code at country level, the region
 * long name (`"Bayern"`, `"St. Gallen"`) at state level, the postcode at postcode level. State
 * lookups match on `iso3166_2` instead, which is language-independent and immune to spelling
 * differences.
 */
export interface BevHolderRecord {
  level: BevHolderLevel;
  regionCode: string;
  /** The state lookup's index key (`"DE-BY"`, `"CH-SG"`, `"AT-3"`); `null` at other levels. */
  iso3166_2: string | null;
  /** `null` at country level, where `regionCode` holds it. */
  country: string | null;
  /** Set on postcode records only. */
  state: string | null;
  vehicleClass: string;
  commercialStock: number | null;
  commercialBev: number | null;
  commercialBevShare: number | null;
  privateStock: number | null;
  privateBev: number | null;
  privateBevShare: number | null;
  totalStock: number | null;
  totalBev: number | null;
  totalBevShare: number | null;
}

/** The `state` slot carries the ISO 3166-2 code, not the region name, so matching is
 * name-independent across DACH. */
export interface BevHoldersQuery {
  country: string;
  state: string;
  postcode: string;
}

/**
 * A site outside the covered regions, an unmapped ISO code, or an unlisted postcode (CH and AT
 * carry no postcode rows) yields `null` for that level rather than an error.
 */
export interface BevHoldersResult {
  country: BevHolderRecord | null;
  state: BevHolderRecord | null;
  postcode: BevHolderRecord | null;
}

/**
 * Parsed at the API boundary. Requires the server to cast the share columns to `float`:
 * Laravel's `decimal` emits JSON *strings* and would fail this parse on every populated slot.
 */
export const bevHolderRecordSchema = z.object({
  level: z.enum(BEV_HOLDER_LEVELS),
  regionCode: z.string(),
  iso3166_2: z.string().nullable(),
  country: z.string().nullable(),
  state: z.string().nullable(),
  vehicleClass: z.string(),
  commercialStock: z.number().nullable(),
  commercialBev: z.number().nullable(),
  commercialBevShare: z.number().nullable(),
  privateStock: z.number().nullable(),
  privateBev: z.number().nullable(),
  privateBevShare: z.number().nullable(),
  totalStock: z.number().nullable(),
  totalBev: z.number().nullable(),
  totalBevShare: z.number().nullable(),
}) satisfies z.ZodType<BevHolderRecord>;

export const bevHoldersResultSchema = z.object({
  country: bevHolderRecordSchema.nullable(),
  state: bevHolderRecordSchema.nullable(),
  postcode: bevHolderRecordSchema.nullable(),
}) satisfies z.ZodType<BevHoldersResult>;
